class SubscriptionItem {
    addCallback(
        callback: (id: string, stampData: string | null | undefined) => void
    ) {
        if (this.callbacks.indexOf(callback) !== -1) return;
        this.callbacks.push(callback);
    }
    id: number;
    callbacks: ((id: string, stampData: string | null | undefined) => void)[];
    constructor(id: number) {
        this.id = id;
        this.callbacks = [];
    }
}

import { getWebsocketUrl } from "./auction-socket-const";
import * as networkState from "../../utilities/network-state";
import { type AppendIdArgs } from "./types";

export class AuctionSocket {
    private url: string | null = null;
    private subscribedItems: Array<SubscriptionItem> = [];
    private onConnectionCallbacks: Array<(hasConnection: boolean) => void> = [];
    private retriesLeft: number = 5;
    private ws: WebSocket | null;
    private disable = false;
    private invokeConnectionCallbacks(hasConnection: boolean) {
        for (let i = 0; i < this.onConnectionCallbacks.length; i++) {
            const callback = this.onConnectionCallbacks[i];
            try {
                callback(hasConnection);
            } catch (e) {
                console.error(e);
            }
        }
    }

    constructor(url: string) {
        this.url = url;
        this.ws = null;

        networkState.onOnline(() => {
            this.ensureInit();
            this.triggerAll();
        });

        setInterval(() => {
            if (this.subscribedItems.length > 0 && this.disable === false) {
                this.ensureInit();
            }
        }, 1000 * 60);
    }

    private idToNumber(id: any) {
        return parseInt(id, 10);
    }

    private getItem(id: number): SubscriptionItem | undefined {
        if (isNaN(id)) return undefined;
        return this.subscribedItems.find((x) => x.id == id);
    }

    public containsId(id: any) {
        const productId = this.idToNumber(id);
        return this.getItem(productId) != null;
    }

    private append(id: number): SubscriptionItem {
        const item = new SubscriptionItem(id);
        this.subscribedItems.push(item);
        this.ensureInit();
        if (this.ws != null && this.ws.readyState === this.ws.OPEN) {
            this.ws.send("subscribe:" + id);
        }
        return item;
    }

    public appendId(...[id, callback]: AppendIdArgs): void {
        const productId = this.idToNumber(id);
        if (isNaN(productId)) return;

        let item = this.getItem(productId);
        if (item == null) {
            item = this.append(productId);
        }
        item.addCallback(callback);
    }

    public triggerId(id: any) {
        this.handleUpdate(id.toString(), undefined);
    }

    public triggerAll() {
        for (const item of this.subscribedItems) {
            this.triggerId(item.id);
        }
    }

    public onConnectionChange(
        callback: (hasConnection: boolean) => void
    ): void {
        this.onConnectionCallbacks.push(callback);
    }

    private ensureInit(): void {
        if (this.disable) return;
        if (
            this.ws == null ||
            typeof this.ws != "object" ||
            this.ws.readyState === this.ws.CLOSED ||
            this.ws.readyState === this.ws.CLOSING
        ) {
            this.initWs();
        }
    }

    private initWs(): void {
        if (this.disable) return;
        const result = this.createNewWebSocket();
        if (result instanceof Error) {
            console.error("Failed to initialize websocket");
            console.error(result);
            return;
        }
        this.ws = result;
    }

    stop() {
        this.disable = true;
        this.ws?.close();
    }

    private createNewWebSocket(): WebSocket | Error {
        if (this.url == null)
            throw new Error("Can't initialize websocket. No url provided!");
        try {
            const ws = new WebSocket(this.url);
            ws.addEventListener("error", (er) => console.log(er));
            const auctionSocket = this;
            ws.addEventListener("open", function () {
                auctionSocket.wsOnOpen(this);
            });
            ws.addEventListener("message", (event) =>
                auctionSocket.wsOnMessage(event)
            );
            ws.addEventListener("close", (event) =>
                auctionSocket.wsOnClose(event)
            );
            return ws;
        } catch (e) {
            let err = e;
            if (typeof err === "string") {
                err = new Error(err);
            }

            if (err instanceof Error) {
                return err;
            } else {
                return new Error("Unknown error: " + err);
            }
        }
    }

    private wsOnOpen(ws: WebSocket): void {
        this.invokeConnectionCallbacks(true);
        for (let i = 0; i < this.subscribedItems.length; i++) {
            ws.send("subscribe:" + this.subscribedItems[i].id);
        }
    }

    private wsOnMessage(event: any): void {
        try {
            this.handleMessage(event.data);
        } catch (e) {
            console.error(e);
        }
    }

    private handleMessage(message: string): void {
        const data = message.split(":");

        const [command] = data;

        if (command.toLowerCase() === "update") {
            const [, id, stampData] = data;
            this.handleUpdate(id, stampData);
        }
    }

    private handleUpdate(id: string, stampData: string | null | undefined) {
        const productId = this.idToNumber(id);
        if (isNaN(productId)) {
            console.warn("handleUpdate received invalid id: " + id);
            return;
        }
        const item = this.getItem(productId);
        if (item == null) {
            console.warn("handleUpdate did not find item for id: " + id);
            return;
        }
        for (const callback of item.callbacks) {
            callback(id, stampData);
        }
    }

    private wsOnClose(event: any): void {
        if (this.disable) {
            console.debug("WS closed");
            return;
        }
        if (event.code === 1006 && --this.retriesLeft > 0) {
            // abnormal close
            console.warn("abnormal close, restarting...");
            setTimeout(() => {
                console.log("restart");
                this.initWs();
            }, 1000);
        } else if (event.code === 1006 && this.retriesLeft === 0) {
            console.error("abnormal close, won't try again");
            this.invokeConnectionCallbacks(false);
        }
    }
}

const url = getWebsocketUrl();

export function create() {
    return new AuctionSocket(url);
}

let auctionSocket: AuctionSocket | undefined;

export function get(createNew?: true): AuctionSocket;
export function get(createNew: false): AuctionSocket | undefined;
export function get(createNew = true): AuctionSocket | undefined {
    if (createNew && auctionSocket == null) {
        return (auctionSocket = create());
    }
    return auctionSocket;
}
